import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Chip,
  IconButton,
  Tooltip,
  Button,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove,
  Delete,
  Refresh,
  Add,
  CameraAlt,
  Analytics
} from '@mui/icons-material';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const KeywordSidebar = ({ 
  keywords = [], 
  selectedKeyword, 
  onKeywordSelect, 
  onKeywordDelete, 
  onKeywordRefresh, 
  onAddKeyword,
  onCreateMeasurement,
  loading = false,
  overview = null
}) => {

  const getRankColor = (rank) => {
    if (!rank || rank === 'N/A') return amDesignColors.text.tertiary;
    if (rank <= 3) return amDesignColors.success;
    if (rank <= 10) return amDesignColors.warning;
    return amDesignColors.error;
  };

  const getRankStatus = (rank) => {
    if (!rank || rank === 'N/A') return { text: 'Nicht gefunden', color: amDesignColors.text.tertiary, icon: '❓' };
    if (rank <= 3) return { text: 'Top', color: amDesignColors.success, icon: '🏆' };
    if (rank <= 10) return { text: 'Gut', color: amDesignColors.warning, icon: '👍' };
    return { text: 'Schwach', color: amDesignColors.error, icon: '📈' };
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend === 0) return <Remove sx={{ color: amDesignColors.text.tertiary, fontSize: '1rem' }} />;
    if (trend > 0) return <TrendingUp sx={{ color: amDesignColors.success, fontSize: '1rem' }} />;
    return <TrendingDown sx={{ color: amDesignColors.error, fontSize: '1rem' }} />;
  };

  const getTrendText = (trend) => {
    if (!trend || trend === 0) return '';
    if (trend > 0) return `+${trend}`;
    return `${trend}`;
  };

  // Berechne Trend aus historischen Daten (aktienmäßiger Indikator)
  const calculateRankingTrend = (keyword) => {
    // Simuliere historische Daten oder nutze echte wenn verfügbar
    if (!keyword.rankingHistory || keyword.rankingHistory.length < 2) {
      // Fallback: berechne Trend aus aktuellen Metriken
      const current = keyword.averageRank || 0;
      const best = keyword.bestRank || current;
      
      if (current > 0 && best > 0) {
        const improvement = best - current;
        if (improvement > 2) return 'strong-up';
        if (improvement > 0) return 'up';
        if (improvement < -2) return 'strong-down';
        if (improvement < 0) return 'down';
      }
      return 'neutral';
    }
    
    // Analysiere die letzten 3-5 Datenpunkte für Trend
    const recentHistory = keyword.rankingHistory.slice(-5);
    if (recentHistory.length < 2) return 'neutral';
    
    let improvementCount = 0;
    let declineCount = 0;
    
    for (let i = 1; i < recentHistory.length; i++) {
      const prev = recentHistory[i-1].averageRank;
      const curr = recentHistory[i].averageRank;
      
      if (curr < prev) improvementCount++; // Niedrigerer Rang = besser
      else if (curr > prev) declineCount++;
    }
    
    const trendRatio = improvementCount / (recentHistory.length - 1);
    
    if (trendRatio >= 0.8) return 'strong-up';
    if (trendRatio >= 0.6) return 'up';
    if (trendRatio <= 0.2) return 'strong-down';
    if (trendRatio <= 0.4) return 'down';
    return 'neutral';
  };

  const getTrendIndicator = (keyword) => {
    const trend = calculateRankingTrend(keyword);
    
    const indicators = {
      'strong-up': {
        icon: <TrendingUp sx={{ fontSize: '1rem', color: amDesignColors.success }} />,
        color: amDesignColors.success,
        tooltip: 'Starker Aufwärtstrend',
        text: '📈'
      },
      'up': { 
        icon: <TrendingUp sx={{ fontSize: '1rem', color: amDesignColors.success }} />, 
        color: amDesignColors.success,
        tooltip: 'Aufwärtstrend',
        text: '↗️'
      },
      'neutral': { 
        icon: <Remove sx={{ fontSize: '1rem', color: amDesignColors.text.tertiary }} />, 
        color: amDesignColors.text.tertiary,
        tooltip: 'Stabil',
        text: '➡️'
      },
      'down': { 
        icon: <TrendingDown sx={{ fontSize: '1rem', color: amDesignColors.error }} />, 
        color: amDesignColors.error,
        tooltip: 'Abwärtstrend',
        text: '↘️'
      },
      'strong-down': {
        icon: <TrendingDown sx={{ fontSize: '1rem', color: amDesignColors.error }} />,
        color: amDesignColors.error,
        tooltip: 'Starker Abwärtstrend',
        text: '📉'
      }
    };
    
    return indicators[trend] || indicators.neutral;
  };

  const getLastAnalyzedText = (lastAnalyzed) => {
    if (!lastAnalyzed) return 'nie';

    const now = new Date();
    const analyzed = new Date(lastAnalyzed);
    const diffInHours = Math.floor((now - analyzed) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'jetzt';
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d`;
  };


  // Ultra-kompakte Keyword-Zeile
  const renderCompactKeywordItem = (keyword) => {
    const isSelected = selectedKeyword === keyword.keyword;
    const rankStatus = getRankStatus(keyword.averageRank);
    const trend = keyword.trend || 0;
    
    return (
      <ListItem key={keyword.keyword} disablePadding sx={{ mb: 0.75 }}>
        <ListItemButton
          selected={isSelected}
          onClick={() => onKeywordSelect(keyword.keyword)}
          sx={{
            ...amDesignComponents.card,
            py: 1,
            px: 1.5,
            minHeight: 'auto',
            borderRadius: '10px',
            border: `1px solid ${amDesignColors.border}`,
            backgroundColor: isSelected ? `${amDesignColors.primary}10` : amDesignColors.white,
            '&.Mui-selected': {
              backgroundColor: `${amDesignColors.primary}10`,
              borderColor: rankStatus.color,
              borderWidth: '2px'
            },
            '&:hover': {
              backgroundColor: isSelected ? `${amDesignColors.primary}10` : amDesignColors.paper,
              borderColor: isSelected ? amDesignColors.primary : amDesignColors.border,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {/* Icon + Keyword Name */}
          <Box display="flex" alignItems="center" flex={1} minWidth={0}>
            <Box 
              sx={{ 
                width: 44,
                height: 45,
                borderRadius: '20%',
                backgroundColor: rankStatus.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                flexShrink: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            >
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: amDesignColors.white }}>
                {keyword.averageRank ? Math.round(keyword.averageRank) : '?'}
              </Typography>
            </Box>
            
            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? amDesignColors.primary : amDesignColors.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: isSelected ? amDesignTypography.fontFamily.heading : amDesignTypography.fontFamily.body
                }}
              >
                {keyword.keyword}
              </Typography>
            </Box>
          </Box>

          {/* Compact Stats */}
          <Box display="flex" alignItems="center" gap={0.5} ml={1}>
            {/* Aktienmäßiger Trend-Indikator */}
            <Box 
              display="flex" 
              alignItems="center" 
              sx={{ 
                p: 0.25,
                borderRadius: 0.5,
                backgroundColor: `${getTrendIndicator(keyword).color}15`,
                border: `1px solid ${getTrendIndicator(keyword).color}30`
              }}
              title={getTrendIndicator(keyword).tooltip}
            >
              {getTrendIndicator(keyword).icon}
            </Box>
            
            {/* Legacy Trend (falls vorhanden) */}
            {trend !== 0 && (
              <Box display="flex" alignItems="center">
                {getTrendIcon(trend)}
                <Typography sx={{ fontSize: '0.7rem', color: trend > 0 ? amDesignColors.success : amDesignColors.error }}>
                  {getTrendText(trend)}
                </Typography>
              </Box>
            )}
            
            {/* Last Analyzed */}
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {getLastAnalyzedText(keyword.lastAnalyzed)}
            </Typography>
            
            {/* Actions */}
            <Box display="flex" gap={0.5}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onKeywordRefresh(keyword.keyword);
                }}
                disabled={loading}
                sx={{
                  p: 0.5,
                  backgroundColor: `${amDesignColors.primary}15`,
                  '&:hover': {
                    backgroundColor: `${amDesignColors.primary}25`
                  }
                }}
              >
                <Refresh sx={{ fontSize: '1rem', color: amDesignColors.primary }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onKeywordDelete(keyword.keyword);
                }}
                sx={{
                  p: 0.5,
                  backgroundColor: `${amDesignColors.error}15`,
                  '&:hover': {
                    backgroundColor: `${amDesignColors.error}25`
                  }
                }}
              >
                <Delete sx={{ fontSize: '1rem', color: amDesignColors.error }} />
              </IconButton>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Add Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, fontFamily: amDesignTypography.fontFamily.heading, color: amDesignColors.text.primary }}>
            Keywords
          </Typography>
          <Typography variant="caption" sx={{ color: amDesignColors.text.secondary, fontSize: '0.9rem', fontWeight: 500 }}>
            · {keywords.length}
          </Typography>
        </Box>
        <Button
          onClick={onAddKeyword}
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: amDesignColors.primary,
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              opacity: 0.8
            }
          }}
          startIcon={<Add sx={{ fontSize: '1.1rem', mr: 0.5 }} />}
        >
          Hinzufügen
        </Button>
      </Box>

      {/* Keywords Liste */}
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
        {keywords.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 2,
            color: amDesignColors.text.secondary
          }}>
            <Analytics sx={{ fontSize: 40, mb: 2, color: amDesignColors.text.tertiary, opacity: 0.6 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 2, textAlign: 'center', color: amDesignColors.text.secondary }}>
              Noch keine Keywords analysiert
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', mb: 3, textAlign: 'center', color: amDesignColors.text.tertiary }}>
              Starten Sie mit Ihrer ersten Keyword-Analyse
            </Typography>
            
            {/* Enterprise-Grade Add Keyword Button - Properly Centered */}
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={onAddKeyword}
              sx={{
                ...amDesignComponents.button,
                mt: 2,
                px: 4,
                py: 1.5,
                borderRadius: '10px',
                fontSize: '0.9rem',
                backgroundColor: amDesignColors.primary,
                color: amDesignColors.white,
                boxShadow: `0 4px 12px ${amDesignColors.primary}40`,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: amDesignColors.primaryDark,
                  boxShadow: `0 6px 16px ${amDesignColors.primary}50`,
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Erstes Keyword hinzufügen
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {keywords.map(renderCompactKeywordItem)}
          </List>
        )}
      </Box>

      {/* Elegante Action-Buttons unten */}
      <Box sx={{
        borderTop: `1px solid ${amDesignColors.border}`,
        pt: 1.5,
        mt: 'auto'
      }}>
        <Stack direction="row" spacing={1}>
          {/* Add Keyword Button */}
          <Button
            onClick={onAddKeyword}
            variant="text"
            startIcon={<Add />}
            sx={{
              ...amDesignComponents.button,
              flex: 1,
              color: amDesignColors.text.secondary,
              backgroundColor: 'transparent',
              border: `1px dashed ${amDesignColors.border}`,
              py: 1,
              fontSize: '0.8rem',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: amDesignColors.paper,
                borderColor: amDesignColors.primary,
                color: amDesignColors.primary,
                borderStyle: 'solid'
              }
            }}
          >
            Keyword hinzufügen
          </Button>

          {/* Measurement Button */}
          <Tooltip title="Neue Messung für alle Keywords">
            <IconButton
              onClick={onCreateMeasurement}
              disabled={loading}
              sx={{
                color: amDesignColors.text.secondary,
                border: `1px dashed ${amDesignColors.border}`,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: amDesignColors.paper,
                  borderColor: amDesignColors.primary,
                  color: amDesignColors.primary,
                  borderStyle: 'solid'
                }
              }}
            >
              <CameraAlt />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

export default KeywordSidebar;